import {
  BarChart3,
  Building2,
  ChevronDown,
  Clock,
  LayoutDashboard,
  LogOut,
  Menu,
  ReceiptText,
  ShoppingCart,
  X,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import logoFrancoFrutt from '../assets/logoFrancoFrutt.png'
import PwaInstallButton from '../components/pwa/PwaInstallButton'
import { useAuth } from '../context/AuthContext'
import { useEmpresa } from '../context/EmpresaContext'
import { gruposDeRuta, MENU_ADMIN } from './menuAdmin'

function AppLayout() {
  const { user, cerrarSesion } = useAuth()
  const location = useLocation()

  const {
    empresas,
    empresaActual,
    seleccionarEmpresa,
  } = useEmpresa()

  const esPosVenta = empresaActual?.rol === 'pos_venta'

  const [gruposAbiertos, setGruposAbiertos] = useState(() =>
    gruposDeRuta(location.pathname)
  )
  const [menuAbierto, setMenuAbierto] = useState(false)

  useEffect(() => {
    setMenuAbierto(false)

    // Al llegar a una pantalla (también por enlace directo) se abre su grupo.
    setGruposAbiertos((actuales) => {
      const faltantes = gruposDeRuta(location.pathname).filter(
        (id) => !actuales.includes(id)
      )

      return faltantes.length > 0 ? [...actuales, ...faltantes] : actuales
    })
  }, [location.pathname])

  const alternarGrupo = (id) => {
    setGruposAbiertos((actuales) =>
      actuales.includes(id)
        ? actuales.filter((grupo) => grupo !== id)
        : [...actuales, id]
    )
  }

  const estiloEnlace = ({ isActive }) =>
    [
      'flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold transition',
      isActive
        ? 'bg-emerald-600 text-white'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    ].join(' ')

  const estiloSubenlace = ({ isActive }) =>
    [
      'flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-semibold transition',
      isActive
        ? 'bg-emerald-600 text-white'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    ].join(' ')

  return (
    <div className="min-h-screen bg-slate-100">
      {menuAbierto && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={() => setMenuAbierto(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col bg-white transition-transform duration-300 lg:z-30 lg:w-64 lg:max-w-none lg:translate-x-0 lg:border-r lg:border-slate-200 ${
          menuAbierto ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-20 shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-6">
          <Link
            to="/"
            className="flex min-w-0 items-center gap-3"
          >
            <img
              src={logoFrancoFrutt}
              alt="Franco Frutt"
              className="h-12 w-auto shrink-0 object-contain"
            />

            <p className="truncate text-xs font-semibold uppercase tracking-wide text-slate-500">
              {esPosVenta ? 'POS' : 'Administración'}
            </p>
          </Link>

          <button
            type="button"
            onClick={() => setMenuAbierto(false)}
            aria-label="Cerrar menú"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 lg:hidden"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav className="space-y-2 p-4">
            {esPosVenta ? (
              <>
                <p className="px-4 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  POS / Ventas
                </p>

                <NavLink
                  to="/pos/nueva-venta"
                  className={estiloEnlace}
                >
                  <ShoppingCart size={20} />
                  Nueva venta
                </NavLink>

                <NavLink
                  to="/pos/ventas"
                  className={estiloEnlace}
                >
                  <ReceiptText size={20} />
                  Ventas
                </NavLink>

                <NavLink
                  to="/pos/pagos-pendientes"
                  className={estiloEnlace}
                >
                  <Clock size={20} />
                  Pagos pendientes
                </NavLink>

                <NavLink
                  to="/pos/analitica"
                  className={estiloEnlace}
                >
                  <BarChart3 size={20} />
                  Analítica POS
                </NavLink>
              </>
            ) : (
              <>
                <NavLink
                  to="/"
                  end
                  className={estiloEnlace}
                >
                  <LayoutDashboard size={20} />
                  Inicio
                </NavLink>

                {MENU_ADMIN.map((grupo) => {
                  const IconoGrupo = grupo.icono

                  if (grupo.to) {
                    return (
                      <NavLink
                        key={grupo.id}
                        to={grupo.to}
                        className={estiloEnlace}
                      >
                        <IconoGrupo size={20} />
                        {grupo.titulo}
                      </NavLink>
                    )
                  }

                  const abierto = gruposAbiertos.includes(grupo.id)

                  return (
                    <div
                      key={grupo.id}
                      className="pt-2"
                    >
                      <button
                        type="button"
                        onClick={() => alternarGrupo(grupo.id)}
                        aria-expanded={abierto}
                        className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                      >
                        <span className="flex items-center gap-3">
                          <IconoGrupo size={20} />
                          {grupo.titulo}
                        </span>

                        <ChevronDown
                          size={18}
                          className={`transition-transform ${
                            abierto ? 'rotate-180' : ''
                          }`}
                        />
                      </button>

                      {abierto && (
                        <div className="mt-1 space-y-1 border-l border-slate-200 pl-3">
                          {grupo.secciones.map((seccion, indice) => (
                            <div
                              key={seccion.titulo || indice}
                              className="space-y-1"
                            >
                              {seccion.titulo &&
                                seccion.titulo !==
                                  seccion.items[0].etiqueta && (
                                  <p className="px-4 pb-0.5 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                                    {seccion.titulo}
                                  </p>
                                )}

                              {seccion.items.map((item) => {
                                const IconoItem = item.icono

                                return (
                                  <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.exacto}
                                    className={estiloSubenlace}
                                  >
                                    <IconoItem size={18} />
                                    {item.etiqueta}
                                  </NavLink>
                                )
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </>
            )}

            <PwaInstallButton className="mt-2 flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50" />
          </nav>

          {empresas.length > 1 && (
            <div className="border-t border-slate-200 p-4">
              <label
                htmlFor="empresa-activa"
                className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-400"
              >
                Empresa activa
              </label>

              <select
                id="empresa-activa"
                value={empresaActual?.id || ''}
                onChange={(event) =>
                  seleccionarEmpresa(event.target.value)
                }
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              >
                {empresas.map((empresa) => (
                  <option
                    key={empresa.id}
                    value={empresa.id}
                  >
                    {empresa.nombre_fantasia}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-200 p-4">
          <div className="mb-3 min-w-0">
            <p className="truncate text-sm font-semibold text-slate-700">
              {user?.email}
            </p>

            <p className="text-xs capitalize text-slate-500">
              {empresaActual?.rol}
            </p>
          </div>

          <button
            type="button"
            onClick={cerrarSesion}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            <LogOut size={18} />
            Salir
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
          <div className="flex min-h-16 items-center gap-3 px-4 sm:px-6 lg:min-h-20">
            <button
              type="button"
              onClick={() => setMenuAbierto(true)}
              aria-label="Abrir menú"
              className="shrink-0 text-slate-600 transition hover:text-slate-900 lg:hidden"
            >
              <Menu size={24} />
            </button>

            <Link
              to="/"
              className="shrink-0 lg:hidden"
            >
              <img
                src={logoFrancoFrutt}
                alt="Franco Frutt"
                className="h-8 w-auto object-contain"
              />
            </Link>

            <div className="flex min-w-0 flex-1 items-center gap-3">
              <Building2
                size={22}
                className="hidden shrink-0 text-emerald-600 lg:block"
              />

              <div className="min-w-0">
                <p className="text-xs text-slate-500">
                  Empresa activa
                </p>

                <p className="truncate text-sm font-semibold text-slate-800">
                  {empresaActual?.nombre_fantasia}
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="w-full px-4 py-4 sm:px-5 md:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default AppLayout
